import { assertConfigured } from "./config"
import { readPasswordResetError } from "./sessionApi"
import type {
  AuthConfig,
  AuthSession,
  DashboardMfaEnrollment,
  DashboardMfaMode,
  SessionResponse,
} from "./types"

export type PasswordLoginResult =
  | {
      result: "authenticated"
      session: AuthSession
    }
  | {
      result: "mfa_required"
      mode: DashboardMfaMode
      enrollment?: DashboardMfaEnrollment
    }

export async function performPasswordLoginAction(
  config: AuthConfig,
  email: string,
  password: string,
  rememberMe = false
): Promise<PasswordLoginResult> {
  assertConfigured(config)

  const response = await fetch("/api/dashboard/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ email, password, rememberMe }),
  })
  const payload = (await response.json()) as Partial<SessionResponse>
  if (!response.ok) {
    throw new Error(payload.error ?? "Could not sign in.")
  }

  if (payload.mfaEnrollmentRequired) {
    return { result: "mfa_required", mode: "enroll", enrollment: payload.enrollment }
  }

  if (payload.mfaRequired) {
    return { result: "mfa_required", mode: "challenge" }
  }

  const nextSession = await fetchServerSession()
  return { result: "authenticated", session: nextSession }
}

export async function performMfaAction(config: AuthConfig, code: string): Promise<AuthSession> {
  assertConfigured(config)

  const response = await fetch("/api/dashboard/auth/mfa", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ type: "otp", code }),
  })
  const payload = (await response.json()) as Partial<SessionResponse>
  if (!response.ok) {
    throw new Error(payload.error ?? "Could not verify MFA code.")
  }

  return await fetchServerSession()
}

export async function requestPasswordResetAction(config: AuthConfig, email: string, locale?: "en" | "ja") {
  assertConfigured(config)

  const response = await fetch("/api/dashboard/auth/password-reset", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({
      email,
      locale,
    }),
  })

  if (!response.ok) {
    throw new Error(await readPasswordResetError(response))
  }
}

export async function performSessionRefreshAction() {
  const response = await fetch("/api/dashboard/auth/refresh", {
    method: "POST",
    credentials: "same-origin",
  })
  if (!response.ok) return null

  const payload = (await response.json()) as SessionResponse
  if (!payload.authenticated) return null

  return {
    user: payload.user,
    expiresAt: payload.expiresAt,
    idleExpiresAt: payload.idleExpiresAt,
    absoluteExpiresAt: payload.absoluteExpiresAt,
  } as AuthSession
}

export async function performLogoutAction() {
  return fetch("/api/dashboard/auth/logout", { method: "POST", credentials: "same-origin" })
}

async function fetchServerSession(): Promise<AuthSession> {
  const response = await fetch("/api/dashboard/auth/session", {
    method: "GET",
    credentials: "same-origin",
  })
  if (!response.ok) {
    throw new Error("Could not read the dashboard session.")
  }

  const payload = (await response.json()) as SessionResponse
  if (!payload.authenticated || !payload.user || typeof payload.user !== "object") {
    throw new Error("Could not read the dashboard session.")
  }

  return {
    user: payload.user,
    expiresAt: payload.expiresAt,
    idleExpiresAt: payload.idleExpiresAt,
    absoluteExpiresAt: payload.absoluteExpiresAt,
  }
}
